// Detector de capacidades + cadena de fallback (WDUR-02).
//
// Orden EXACTO (decidido en la deep research, no re-litigar):
//   1. opfs-sahpool   (OPFS file-backed, en Worker)  — primario
//   2. wa-sqlite-idb  (IDBBatchAtomicVFS)            — fallback final (IndexedDB)
//
// AMBOS tiers son WASM puros, SIN dependencia de Capacitor. El antiguo tier de último recurso
// (sql.js vía jeep-sqlite) se RETIRÓ en la migración a Cordova: dependía de
// @capacitor-community/sqlite, lo que impedía desacoplar el web de Capacitor. wa-sqlite-idb cubre
// prácticamente todos los navegadores con IndexedDB (incluido Safari); si ni OPFS ni IndexedDB
// están disponibles, el modo offline no aplica en ese navegador (el flujo online sigue intacto,
// SYNC-03: el servidor es la red de cero-pérdida).
//
// La deteccion mas fiable de OPFS-sahpool es un PROBE de instalacion real en un worker
// (los feature flags mienten en Safari private: getDirectory() existe pero los sync
// access handles fallan). Cada probe va en try/catch y cae al siguiente tier.
//
// El backend elegido se loguea (WDUR-02) y el boot lo expone en window.__offlineV2 (dev).

import { OpfsSahPoolBackend } from './OpfsSahPoolBackend'
import { WaSqliteIdbBackend } from './WaSqliteIdbBackend'
import { getFlags } from '../../flags'

// opfs-sahpool exige acceso EXCLUSIVO de una sola pestaña/dispositivo a su pool de handles OPFS: una
// segunda pestaña que intente abrirlo aborta el runtime WASM entero con `RuntimeError: unreachable`
// (fatal, irrecuperable, y el reintento indefinido de _ensureWorker vuelve a chocar con el pool tomado
// en cada operación). navigator.locks ya se usa para el mismo tipo de coordinación entre pestañas en
// sync-worker.js (tick, leader election). Aquí el lock debe vivir mientras la pestaña siga usando
// opfs-sahpool (no un solo tick) -> se resuelve `acquired` de una, pero el callback de
// navigator.locks.request se mantiene pendiente hasta que `release()` se llame explícitamente
// (OpfsSahPoolBackend.close()) o el navegador libere el lock solo al cerrar/recargar la pestaña.
const OPFS_LOCK_NAME = 'offline-v2-opfs-sahpool'

function acquireOpfsLock () {
  const hasWebLocks = typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function'
  if (!hasWebLocks || !getFlags().offline_multitab_guard) {
    // Sin soporte de Web Locks, o guard apagado por flag: no se puede/debe coordinar -> comportamiento
    // previo intacto (intenta opfs-sahpool igual, como antes de este fix).
    return Promise.resolve({ acquired: true, release: () => {} })
  }
  let releaseFn = () => {}
  const held = new Promise((res) => { releaseFn = res })
  return new Promise((resolveOuter) => {
    navigator.locks.request(OPFS_LOCK_NAME, { ifAvailable: true }, (lock) => {
      if (!lock) {
        resolveOuter({ acquired: false, release: () => {} })
        return Promise.resolve()
      }
      resolveOuter({ acquired: true, release: releaseFn })
      return held // el lock queda tomado hasta que release() resuelva esta promesa.
    })
  })
}

/**
 * Selecciona y ABRE el motor SQLite web más fuerte disponible, devolviendo el backend YA ABIERTO.
 *
 * FIX (review Hugo): antes se PROBEABA opfs-sahpool con un open+close efímero y LUEGO se creaba una
 * SEGUNDA instancia sobre el mismo pool. `close()` no libera los sync access handles de forma síncrona
 * (se sueltan async en el terminate del worker), así que el open REAL podía encontrarlos tomados y
 * fallar — y como `_doOpen` abría DESPUÉS de seleccionar, un fallo del open real NO caía al siguiente
 * tier: offline muerto toda la sesión. Ahora el "probe" ES el open real (se REUSA el backend abierto:
 * cero doble-init de WASM, cero carrera de release), y si el open de un tier lanza, se CAE al siguiente.
 *
 * @param {object} openOpts  { encrypted, mode } propagadas al backend.
 * @returns {Promise<import('./WebSqliteBackend').WebSqliteBackend>} backend YA ABIERTO.
 */
export async function selectWebBackend (openOpts = {}) {
  // Tier 1: opfs-sahpool (OPFS file-backed en Worker). El open real es la mejor detección (los feature
  // flags mienten en Safari private: getDirectory() existe pero los sync access handles fallan).
  if (typeof Worker !== 'undefined' && navigator.storage && typeof navigator.storage.getDirectory === 'function') {
    const { acquired, release } = await acquireOpfsLock()
    if (acquired) {
      const opfs = new OpfsSahPoolBackend()
      try {
        await opfs.open(openOpts)
        console.info('[offline-v2] web engine =', opfs.name)
        opfs._releaseOpfsLock = release // liberado en OpfsSahPoolBackend.close()
        return opfs
      } catch (e) {
        console.info('[offline-v2] opfs-sahpool no viable, cae a fallback:', String(e && e.message ? e.message : e))
        try { await opfs.close() } catch { /* noop */ }
        release() // el open falló igual -> no dejar el lock tomado sin uso real
      }
    } else {
      console.info('[offline-v2] opfs-sahpool: otra pestaña ya lo tiene abierto, usando wa-sqlite-idb')
    }
  }

  // Tier 2 (fallback final): wa-sqlite IDBBatchAtomicVFS (IndexedDB). Si el open falla aquí, propaga
  // (no hay más tiers) -> el catch del boot lo maneja y reintenta en el próximo foreground.
  if (typeof indexedDB !== 'undefined') {
    const wa = new WaSqliteIdbBackend()
    await wa.open(openOpts)
    console.info('[offline-v2] web engine =', wa.name)
    return wa
  }

  // Ni OPFS-SAH ni IndexedDB: no hay motor SQLite web viable. El modo offline no aplica en este
  // navegador; el flujo online sigue funcionando (SYNC-03). Error explícito para no fallar en silencio.
  throw new Error('[offline-v2] sin backend SQLite web disponible (ni OPFS-SAH ni IndexedDB)')
}
